/**
 * Idle lock: after inactivity the shell locks and requires that account's
 * Team Member Pin (or logout).
 *
 * - Custom employees: enterprise `staffIdleLockMinutes` (default 30 min)
 * - Owners / full admins: enterprise `staffIdleLockAdminMinutes` (0 = never)
 * - Sellers: never locked
 */

import { isAccountOwner, isFullAdminUser, isSellerAccountUser } from "./employees";

export const STAFF_IDLE_LOCK_NEVER = 0;
export const DEFAULT_STAFF_IDLE_LOCK_MINUTES = 30;
export const STAFF_IDLE_LOCK_MINUTE_OPTIONS = [5, 15, 30, 60];
export const STAFF_IDLE_LOCK_ADMIN_MINUTE_OPTIONS = [
  STAFF_IDLE_LOCK_NEVER,
  ...STAFF_IDLE_LOCK_MINUTE_OPTIONS,
];
export const STAFF_IDLE_LOCK_MS = DEFAULT_STAFF_IDLE_LOCK_MINUTES * 60 * 1000;
export const STAFF_IDLE_LOCK_STORAGE_KEY = "riverr_staff_idle_lock_v1";

export function resolveStaffIdleLockMinutes(enterprise) {
  const raw = Number(enterprise?.staffIdleLockMinutes);
  if (STAFF_IDLE_LOCK_MINUTE_OPTIONS.includes(raw)) return raw;
  return DEFAULT_STAFF_IDLE_LOCK_MINUTES;
}

export function resolveStaffIdleLockAdminMinutes(enterprise) {
  const raw = Number(enterprise?.staffIdleLockAdminMinutes);
  if (STAFF_IDLE_LOCK_ADMIN_MINUTE_OPTIONS.includes(raw)) return raw;
  return STAFF_IDLE_LOCK_NEVER;
}

/** @deprecated Prefer resolveStaffIdleLockMsForUser(user, enterprise) */
export function resolveStaffIdleLockMs(enterprise) {
  return resolveStaffIdleLockMinutes(enterprise) * 60 * 1000;
}

export function resolveStaffIdleLockMsForUser(user, enterprise) {
  if (!user?.uid) return 0;
  if (isSellerAccountUser(user)) return 0;
  if (user?.staffAccess?.mode === "custom") {
    return resolveStaffIdleLockMinutes(enterprise) * 60 * 1000;
  }
  if (isAccountOwner(user, enterprise) || isFullAdminUser(user)) {
    const mins = resolveStaffIdleLockAdminMinutes(enterprise);
    return mins > 0 ? mins * 60 * 1000 : 0;
  }
  return 0;
}

export function formatStaffIdleLockLabel(minutes) {
  const n = Number(minutes);
  if (n === STAFF_IDLE_LOCK_NEVER) return "Never";
  if (n === 60) return "1 hour";
  return `${n} minutes`;
}

export function shouldApplyStaffIdleLock(user, enterprise) {
  return resolveStaffIdleLockMsForUser(user, enterprise) > 0;
}

export function staffIdleLockDisplayName(user) {
  const display = String(user?.displayName || "").trim();
  if (display) return display;
  const fromParts = [user?.firstName, user?.lastName]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ");
  if (fromParts) return fromParts;
  const name = String(user?.name || "").trim();
  if (name) return name;
  return String(user?.email || "").trim() || "Team member";
}

function readStorage() {
  try {
    const raw = sessionStorage.getItem(STAFF_IDLE_LOCK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(state) {
  try {
    if (!state) {
      sessionStorage.removeItem(STAFF_IDLE_LOCK_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STAFF_IDLE_LOCK_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearStaffIdleLockStorage() {
  writeStorage(null);
}

/** Restore lock state for this user, or start a fresh activity clock. */
export function loadStaffIdleLockState(uid, timeoutMs = STAFF_IDLE_LOCK_MS) {
  if (!uid) return { locked: false, lastActivityAt: Date.now() };
  const stored = readStorage();
  if (!stored || stored.uid !== uid) {
    const next = { uid, locked: false, lastActivityAt: Date.now() };
    writeStorage(next);
    return next;
  }
  const lastActivityAt = Number(stored.lastActivityAt) || Date.now();
  const locked =
    !!stored.locked || Date.now() - lastActivityAt >= timeoutMs;
  const next = { uid, locked, lastActivityAt };
  if (locked && !stored.locked) writeStorage(next);
  return next;
}

export function persistStaffIdleLockState({ uid, locked, lastActivityAt }) {
  if (!uid) return;
  writeStorage({
    uid,
    locked: !!locked,
    lastActivityAt: Number(lastActivityAt) || Date.now(),
  });
}
